import React from 'react';
import { Clock3, TimerReset } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { AUTO_LOGOUT_OPTIONS } from '../../lib/sessionTimer';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * Badge no header: tempo logado + configuração de auto-logout.
 * O motor (`useOfficeSessionTimer`) fica no OfficeHeader (uma instância).
 */
export default function SessionTimerControl({
  compact = false,
  /** Só ícone — para header mobile estreito (tempo no title/menu). */
  iconOnly = false,
  className,
  elapsedLabel,
  limitMinutes,
  remainingLabel,
  nearEnd,
  setAutoLogoutMinutes,
  resetClock,
  active = true,
}) {
  const { t } = useLanguage();
  if (!active) return null;

  const limitOpt = AUTO_LOGOUT_OPTIONS.find((o) => o.minutes === limitMinutes);
  const tip = `${t('session.tooltip')} · ${elapsedLabel}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={compact || iconOnly ? 'session-timer-btn-mobile' : 'session-timer-btn'}
          title={tip}
          aria-label={tip}
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-[0.45rem] border px-2.5 text-xs font-semibold tabular-nums transition-colors',
            nearEnd
              ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/15 dark:text-amber-300'
              : 'border-border/80 bg-card text-foreground hover:bg-muted',
            compact && !iconOnly && 'h-8 px-2',
            iconOnly &&
              'h-9 w-9 justify-center gap-0 rounded-[0.35rem] border-transparent bg-transparent px-0 hover:bg-muted',
            iconOnly && nearEnd && 'border-amber-500/35 bg-amber-500/10',
            className,
          )}
        >
          <Clock3
            className={cn('h-3.5 w-3.5 shrink-0 text-primary', iconOnly && 'h-[17px] w-[17px]')}
            strokeWidth={1.85}
          />
          {!iconOnly ? <span className="min-w-[3.25rem] text-left">{elapsedLabel}</span> : null}
          {!compact && !iconOnly ? (
            <span className="hidden max-w-[4.5rem] truncate font-medium text-muted-foreground xl:inline">
              {limitMinutes === 0 ? t('session.badgeOff') : remainingLabel}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-72" aria-label={t('session.menuTitle')} data-testid="session-timer-menu">
        <DropdownMenuLabel className="space-y-0.5 font-normal">
          <span className="block text-xs font-semibold text-foreground">{t('session.menuTitle')}</span>
          <span className="block text-[11px] leading-snug text-muted-foreground">
            {t('session.menuHint')}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
          <div className="flex items-center justify-between gap-2">
            <span>{t('session.loggedFor')}</span>
            <span className="font-semibold tabular-nums text-foreground">{elapsedLabel}</span>
          </div>
          {limitMinutes > 0 ? (
            <div className="mt-1 flex items-center justify-between gap-2">
              <span>{t('session.remaining')}</span>
              <span
                className={cn(
                  'font-semibold tabular-nums',
                  nearEnd ? 'text-amber-600 dark:text-amber-300' : 'text-foreground',
                )}
              >
                {remainingLabel}
              </span>
            </div>
          ) : (
            <p className="mt-1 text-[11px]">{t('session.noLimit')}</p>
          )}
          <p className="mt-1.5 text-[10px] text-muted-foreground/90">
            {t('session.currentLimit')}:{' '}
            <span className="font-medium text-foreground">
              {t(limitOpt?.labelKey || 'session.opt.off')}
            </span>
          </p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {t('session.autoLogout')}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={String(limitMinutes)}
          onValueChange={(value) => setAutoLogoutMinutes(Number(value))}
          aria-label={t('session.autoLogout')}
        >
          {AUTO_LOGOUT_OPTIONS.map((opt) => (
            <DropdownMenuRadioItem
              key={opt.minutes}
              value={String(opt.minutes)}
              className="min-h-10 cursor-pointer data-[state=checked]:bg-primary/10 data-[state=checked]:font-semibold data-[state=checked]:text-primary"
            >
              {t(opt.labelKey)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="min-h-10 cursor-pointer gap-2" onSelect={resetClock}>
          <TimerReset className="h-3.5 w-3.5 opacity-70" />
          {t('session.resetClock')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
